import React, { useState } from 'react'
import { Button, ConfirmationModal, Input, SelectAnimalFetchInput, SelectInput } from 'src/components'
import { useStyles } from './styles'
import { PageTitle as SectionTitle } from 'src/components/PageTitle'
import { useAuth } from 'src/contexts/auth'
import { cleanWeightValue, formatDateToInputValue, formatWeight, getNumericValue } from 'src/utils'
import { SelectAnimalImage } from './components/SelectAnimalImage'
import { useGetLotList } from 'src/hooks/api/lot'
import { useGetBreedList } from 'src/hooks/api/breed'
import { useGetPastureList } from 'src/hooks/api/pasture'
import { IAnimal } from 'src/dto/animal.dto'
import {
  useGetBloodGradeList,
  useGetAnimalImage,
  useGetProductiveAnimalList,
  useUpdateAnimal,
  useRemoveAnimal
} from 'src/hooks/api/animal'
import { ANIMAL_CATEGORIES } from 'src/constants/animalCategory'
import { useForm } from 'react-hook-form'
import { CreateAnimalForm, createAnimalSchema } from 'src/@types/validations/Animal'
import { yupResolver } from '@hookform/resolvers/yup'
import { parseWeight } from 'src/pages/Protected/CreateAnimal/utils/functions'
import { toast } from 'react-toastify'
import { useCreateAnimalMovimentation } from 'src/hooks/api/animal/mutations/useCreateAnimalMovimentation'
import { useAnimalWeighting } from '../../../hooks/useAnimalWeighting'
import { useQueryClient } from '@tanstack/react-query'
import { toastSyncSuccess } from 'src/utils/syncToast'

interface IGeneralInformations {
  data?: IAnimal
  openGeneologyTree: () => void
  onCloseModal: () => void
}

export const GeneralInformations = ({ data, openGeneologyTree, onCloseModal }: IGeneralInformations) => {
  const { userFarm: farmId } = useAuth()
  const [displayWeight, setDisplayWeight] = useState<string>(() => parseWeight(data?.pesoAtual!))
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { mutateAsync: createAnimalMovimentation } = useCreateAnimalMovimentation()
  const { data: productiveAnimalHistoric = [] } = useGetProductiveAnimalList(data?.idAnimal!)

  const { calculateAndSaveWeighting } = useAnimalWeighting(productiveAnimalHistoric)
  const { mutateAsync: updateAnimalMutation } = useUpdateAnimal()
  const { mutateAsync: removeAnimalMutation, isPending: isRemoving } = useRemoveAnimal()

  const { data: animalImageData, isLoading: isLoadingAnimalImage } = useGetAnimalImage(data?.idAnimal!)

  const { data: lotList } = useGetLotList()
  const { data: pastureList } = useGetPastureList()
  const { data: breedList } = useGetBreedList()
  const { data: bloodGradeList } = useGetBloodGradeList()

  const queryClient = useQueryClient()

  const {
    setValue,
    watch,
    clearErrors,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<CreateAnimalForm>({
    defaultValues: data,
    resolver: yupResolver(createAnimalSchema)
  })

  const animalImage = animalImageData
    ? animalImageData instanceof Blob
      ? URL.createObjectURL(animalImageData)
      : animalImageData
    : null

  const styles = useStyles()

  const handleUpdateAnimalDetails = async (formData: CreateAnimalForm): Promise<void> => {
    const originalAnimalData = data

    try {
      const dataToUpdate = {
        ...originalAnimalData,
        ...formData
      }

      const loteChanged = originalAnimalData?.idLoteAtual !== dataToUpdate.idLoteAtual && !!dataToUpdate.idLoteAtual
      const pastoChanged = originalAnimalData?.idPastoAtual !== dataToUpdate.idPastoAtual && !!dataToUpdate.idPastoAtual

      const weightChanged =
        originalAnimalData?.pesoAtual !== dataToUpdate.pesoAtual &&
        dataToUpdate.pesoAtual !== undefined &&
        dataToUpdate.pesoAtual !== null

      if (loteChanged || pastoChanged) {
        try {
          await createAnimalMovimentation({
            data: new Date().toISOString(),
            idAnimal: originalAnimalData?.idAnimal!,
            nome: originalAnimalData?.nome!,

            loteOrigem: originalAnimalData?.idLoteAtual!,
            loteDestino: dataToUpdate.idLoteAtual!,

            pastoOrigem: originalAnimalData?.idPastoAtual!,
            pastoDestino: dataToUpdate.idPastoAtual!
          })
        } catch (err) {
          console.error('Erro ao criar histórico de movimentação:', err)
        }
      }

      if (weightChanged) {
        await calculateAndSaveWeighting(dataToUpdate as any)
      }

      await updateAnimalMutation(dataToUpdate)
      toastSyncSuccess('Animal atualizado com sucesso')
    } catch (error) {
      console.error(error)
      toast.error('Erro ao atualizar animal')
    }
  }

  const onSubmit = async (formData: CreateAnimalForm) => {
    await handleUpdateAnimalDetails(formData)
    queryClient.invalidateQueries({ queryKey: ['animalList', farmId] })
    queryClient.invalidateQueries({ queryKey: ['getAnimalById', farmId, data?.idAnimal] })
  }

  const handleRemoveAnimal = async () => {
    if (!data) return
    try {
      await removeAnimalMutation(data)
      toastSyncSuccess('Animal removido com sucesso')
      setShowDeleteConfirm(false)
      onCloseModal()
    } catch (error) {
      console.error(error)
      toast.error('Erro ao remover animal')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section>
        <div className={styles.generalInformationContainer}>
          <SelectAnimalImage
            animalImage={animalImage}
            farmId={farmId!}
            idAnimal={data?.idAnimal as string}
            isLoadingAnimalImage={isLoadingAnimalImage}
          />

          <div className={styles.generalInformationForm}>
            <SelectInput
              className={styles.selectInputButton}
              id={'categoria'}
              label='Condição do Animal'
              selectBy='id'
              showBy={['descricao']}
              options={ANIMAL_CATEGORIES}
              value={watch('categoria') || 'AT'}
              placeholder='Selecione a condição do animal'
              onChange={(value) => setValue('categoria', value.id === 'AT' ? null : value.id, { shouldDirty: true })}
              error={errors.categoria?.message}
            />

            <Input
              id={'cdn'}
              label='Número'
              value={watch('cdn') || ''}
              onChange={(value) => setValue('cdn', value, { shouldDirty: true })}
              error={errors.cdn?.message}
            />

            <SelectInput
              className={styles.selectInputButton}
              id={'raca'}
              label='Raça'
              selectBy='idRaca'
              showBy={['descricao']}
              options={breedList!}
              value={watch('raca') || ''}
              placeholder='Selecione uma raça'
              onChange={(value) => setValue('raca', value.idRaca, { shouldDirty: true })}
              error={errors.raca?.message}
            />

            <SelectInput
              className={styles.selectInputButton}
              id={'sangue'}
              label='Grau'
              selectBy='idSangue'
              showBy={['descricao']}
              options={bloodGradeList!}
              value={watch('sangue') || ''}
              placeholder='Selecione um grau'
              onChange={(value) => setValue('sangue', value.idSangue, { shouldDirty: true })}
              error={errors.sangue?.message}
            />

            <Input
              id={'dataNascimento'}
              type='date'
              max='2999-12-31'
              label='Data de nascimento'
              value={formatDateToInputValue(watch('dataNascimento') || '')}
              onChange={(value) => {
                const newDate = new Date(value).toISOString()
                setValue('dataNascimento', newDate, { shouldDirty: true })
                clearErrors('dataNascimento')
              }}
              error={errors.dataNascimento?.message}
            />
          </div>
        </div>

        <div className={styles.generalInformationForm}>
          <div className={styles.generalInformationContainer}>
            <Input
              className={styles.input}
              id={'rgn'}
              label='RGN'
              value={watch('rgn') || ''}
              onChange={(value: string) => {
                setValue('rgn', value, { shouldDirty: true })
                clearErrors('rgn')
              }}
              error={errors.rgn?.message}
            />
            <Input
              className={styles.input}
              id={'rgd'}
              label='RGD'
              type='text'
              value={watch('rgd') || ''}
              onChange={(value: string) => {
                const formatedValue = value.replace(/\D/g, '')
                setValue('rgd', formatedValue, { shouldDirty: true })
                clearErrors('rgd')
              }}
              error={errors.rgd?.message}
            />
            <Input
              className={styles.input}
              id={'avalGene'}
              type='text'
              label='N. de avaliação genética'
              value={watch('avalGene') || ''}
              onChange={(value: string) => {
                const formatedValue = value.replace(/\D/g, '')
                setValue('avalGene', formatedValue, { shouldDirty: true })
                clearErrors('avalGene')
              }}
              error={errors.avalGene?.message}
            />
          </div>
          <Input
            id={'nome'}
            label='Nome'
            value={watch('nome') || ''}
            onChange={(value: string) => {
              setValue('nome', value, { shouldDirty: true })
              clearErrors('nome')
            }}
            error={errors.nome?.message}
          />
          <Input
            id={'peso'}
            label='Peso'
            type='text'
            value={displayWeight}
            onChange={(value: string) => {
              const cleanValue = cleanWeightValue(value)

              const formatted = formatWeight(cleanValue)
              setDisplayWeight(formatted)

              const numericValue = getNumericValue(formatted)
              setValue('pesoAtual', numericValue, { shouldDirty: true })
              clearErrors('pesoAtual')
            }}
            error={errors.pesoAtual?.message}
          />

          <SelectInput
            className={styles.selectInputButton}
            id={'idLoteAtual'}
            label='Lote'
            selectBy='idLote'
            showBy={['descricao']}
            options={lotList!}
            value={watch('idLoteAtual') || ''}
            placeholder='Selecionar Lote do Animal'
            onChange={(value) => setValue('idLoteAtual', value.idLote, { shouldDirty: true })}
            error={errors.idLoteAtual?.message}
          />

          <SelectInput
            className={styles.selectInputButton}
            id={'touro'}
            label='Lote do Touro'
            selectBy='idLote'
            showBy={['descricao']}
            options={lotList!}
            value={watch('idFazendaPai') || ''}
            placeholder='Selecionar Lote do Touro'
            onChange={(value) => setValue('idFazendaPai', value.idLote, { shouldDirty: true })}
            error={errors.idFazendaPai?.message}
          />

          <SelectInput
            className={styles.selectInputButton}
            id={'pasto'}
            label='Pasto'
            selectBy='idPasto'
            showBy={['descricao']}
            options={pastureList!}
            value={watch('idPastoAtual') || ''}
            placeholder='Selecione o pasto do animal'
            onChange={(value) => setValue('idPastoAtual', value.idPasto, { shouldDirty: true })}
            error={errors.idPastoAtual?.message}
          />
        </div>
      </section>
      <section>
        <SectionTitle className={styles.sectionName}>Genelogia</SectionTitle>
        <div className={styles.generalFamilyInformationForm}>
          <SelectAnimalFetchInput
            className={styles.selectInputButton}
            id={'idAnimalMae'}
            label='Mãe'
            selectBy='idAnimal'
            name={'idAnimalMae'}
            showBy={['nome', 'brinco']}
            filteredSex={'Fêmea'}
            value={watch('idAnimalMae') || ''}
            placeholder='Selecione a mãe do animal'
            onChange={(value) => {
              if (!value) {
                setValue('idAnimalMae', null)
                return
              }
              setValue('idAnimalMae', value.id, { shouldDirty: true })
              clearErrors('idAnimalMae')
            }}
            error={errors.idAnimalMae?.message}
          />

          <SelectAnimalFetchInput
            className={styles.selectInputButton}
            id={'idAnimalPai'}
            label='Pai'
            selectBy='idAnimal'
            name={'idAnimalPai'}
            showBy={['nome', 'brinco']}
            filteredSex={'Macho'}
            value={watch('idAnimalPai') || ''}
            placeholder='Selecione o pai do animal'
            onChange={(value) => {
              if (!value) {
                setValue('idAnimalPai', null)
                return
              }
              setValue('idAnimalPai', value.id, { shouldDirty: true })
              clearErrors('idAnimalPai')
            }}
            error={errors.idAnimalPai?.message}
          />

          {/* <Input
            name={'mae'}
            id={'mae'}
            label='Mãe'
            placeholder='Vaca mimos'
            value={data?.raca}
            updateAnimalDetailsValue={updateAnimalDetailsValue}
            handleUpdateAnimalDetails={handleUpdateAnimalDetails}
          />
          <Input
            name={'pai'}
            id={'pai'}
            label='Pai'
            placeholder='Boi bandido'
            value={data?.grau}
            updateAnimalDetailsValue={updateAnimalDetailsValue}
            handleUpdateAnimalDetails={handleUpdateAnimalDetails}
          /> */}
        </div>
        <div className={styles.genealogicalButtonContainer}>
          <Button type='button' outlined onClick={openGeneologyTree}>
            Ver toda árvore genealógica
          </Button>

          <div className={styles.actionButtons}>
            <Button
              type='button'
              onClick={() => setShowDeleteConfirm(true)}
              style={{ backgroundColor: '#D32F2F', color: '#fff', border: 'none' }}
            >
              Excluir
            </Button>

            <Button type='submit' disabled={!isDirty || isSubmitting} isLoading={isSubmitting}>
              Atualizar
            </Button>
          </div>
        </div>
      </section>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title='Excluir animal'
        message='Tem certeza que deseja excluir este animal? Esta ação não poderá ser desfeita.'
        onConfirm={handleRemoveAnimal}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText='Confirmar'
        cancelText='Cancelar'
        isLoading={isRemoving}
      />
    </form>
  )
}
